/**
 * Statuts commandes — libellés FR + clés API (snake_case), ordre = pipeline.
 * Couleurs : select liste commandes (fond + texte + bordure) ; selectChevron = hex sans # pour la flèche.
 * Anciennes valeurs : normalizeLegacyStatus().
 */

export const SALES_ORDER_STATUS_OPTIONS = [
  {
    value: 'pas_encore',
    label: 'Pas encore',
    listSelectClass:
      'border-amber-950 bg-amber-900 text-white hover:bg-amber-800 focus:border-amber-300 focus:ring-amber-400/30',
    selectChevron: 'ffffff'
  },
  {
    value: 'en_cours',
    label: 'En cours',
    listSelectClass:
      'border-amber-500 bg-amber-400 text-amber-950 hover:bg-amber-300 focus:border-amber-600 focus:ring-amber-500/30',
    selectChevron: '78350f'
  },
  {
    value: 'en_attente',
    label: 'En attente',
    listSelectClass:
      'border-orange-600 bg-orange-500 text-white hover:brightness-[1.03] focus:border-white/70 focus:ring-orange-300/40',
    selectChevron: 'ffffff'
  },
  {
    value: 'confirme',
    label: 'Confirmé',
    listSelectClass:
      'border-lime-400 bg-lime-300 text-lime-950 hover:bg-lime-200 focus:border-lime-500 focus:ring-lime-500/30',
    selectChevron: '365314'
  },
  {
    value: 'commande',
    label: 'Commandé',
    listSelectClass:
      'border-blue-700 bg-blue-600 text-white hover:brightness-[1.03] focus:border-blue-300 focus:ring-blue-300/35',
    selectChevron: 'ffffff'
  },
  {
    value: 'arrive',
    label: 'Arrivé',
    listSelectClass:
      'border-sky-500 bg-sky-400 text-sky-950 hover:bg-sky-300 focus:border-sky-600 focus:ring-sky-500/30',
    selectChevron: '0c4a6e'
  },
  {
    value: 'taille',
    label: 'Taillé',
    listSelectClass:
      'border-emerald-900 bg-emerald-800 text-white hover:brightness-[1.03] focus:border-emerald-400 focus:ring-emerald-400/25',
    selectChevron: 'ffffff'
  },
  {
    value: 'pret',
    label: 'Prêt',
    listSelectClass:
      'border-violet-600 bg-violet-500 text-white hover:brightness-[1.03] focus:border-violet-200 focus:ring-violet-200/35',
    selectChevron: 'ffffff'
  },
  {
    value: 'recupere',
    label: 'Récupéré',
    listSelectClass:
      'border-emerald-900 bg-emerald-800 text-white hover:brightness-[1.03] focus:border-emerald-400 focus:ring-emerald-400/25',
    selectChevron: 'ffffff'
  },
  {
    value: 'expedie',
    label: 'Expédié',
    listSelectClass:
      'border-slate-300 bg-slate-200 text-slate-800 hover:bg-slate-100 focus:border-slate-400 focus:ring-slate-400/25',
    selectChevron: '334155'
  },
  {
    value: 'livre',
    label: 'Livré',
    listSelectClass:
      'border-emerald-900 bg-emerald-800 text-white hover:brightness-[1.03] focus:border-emerald-400 focus:ring-emerald-400/25',
    selectChevron: 'ffffff'
  },
  {
    value: 'problem',
    label: 'Problem',
    listSelectClass:
      'border-red-700 bg-red-600 text-white hover:brightness-[1.03] focus:border-red-200 focus:ring-red-200/35',
    selectChevron: 'ffffff'
  },
  {
    value: 'nouvelle_prevision',
    label: 'Nouvelle prévision',
    listSelectClass:
      'border-pink-500 bg-pink-400 text-pink-950 hover:bg-pink-300 focus:border-pink-600 focus:ring-pink-500/30',
    selectChevron: '831843'
  },
  {
    value: 'annule',
    label: 'Annulé',
    listSelectClass:
      'border-neutral-400 bg-neutral-300 text-neutral-800 hover:bg-neutral-200 focus:border-neutral-500 focus:ring-neutral-400/30',
    selectChevron: '404040'
  }
];

const LEGACY_MAP = {
  pending: 'pas_encore',
  confirmed: 'confirme',
  delivered: 'livre',
  cancelled: 'annule',
  amana: 'en_attente',
  expedie_amana: 'expedie',
  livre_collab: 'livre',
  livre_amana: 'livre',
  echange: 'en_cours',
  echange_traite: 'en_cours',
  echange_revenu: 'en_cours',
  retour: 'problem',
  retour_traite: 'en_cours',
  retour_revenu: 'en_cours'
};

/** Statuts « message WhatsApp » modèle livrée / récupérée */
export const WA_LIVREE_STATUS_SET = new Set(['livre', 'recupere']);

/** Statuts pipeline actif (hors tout début / problème / annulé) */
export const WA_EN_COURS_STATUS_SET = new Set([
  'en_cours',
  'en_attente',
  'confirme',
  'commande',
  'arrive',
  'taille',
  'pret',
  'expedie',
  'nouvelle_prevision'
]);

export function normalizeLegacyStatus(status) {
  if (status == null || status === '') return 'pas_encore';
  const s = String(status).trim().toLowerCase();
  if (LEGACY_MAP[s]) return LEGACY_MAP[s];
  if (SALES_ORDER_STATUS_OPTIONS.some((o) => o.value === s)) return s;
  return 'pas_encore';
}

export function getSalesOrderStatusLabel(status) {
  const key = normalizeLegacyStatus(status);
  const opt = SALES_ORDER_STATUS_OPTIONS.find((o) => o.value === key);
  return opt ? opt.label : key;
}

/** Classes Tailwind pour le <select> statut (liste commandes) */
export function getSalesOrderStatusListSelectClass(status) {
  const key = normalizeLegacyStatus(status);
  const opt = SALES_ORDER_STATUS_OPTIONS.find((o) => o.value === key);
  return opt?.listSelectClass
    ?? 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 focus:border-indigo-500 focus:ring-indigo-400/25';
}

/** Couleur stroke SVG flèche (hex 6 sans #) */
export function getSalesOrderStatusSelectChevronHex(status) {
  const key = normalizeLegacyStatus(status);
  const opt = SALES_ORDER_STATUS_OPTIONS.find((o) => o.value === key);
  return opt?.selectChevron ?? '64748b';
}

/** Statuts considérés comme « livré » pour stats / facturation */
export const LIVRE_STATUS_SET = new Set(['livre']);

export function getSalesOrderStatusBadgeVariant(status) {
  const key = normalizeLegacyStatus(status);
  if (key === 'annule') return 'danger';
  if (key === 'problem') return 'danger';
  if (key === 'livre' || key === 'recupere') return 'success';
  if (key === 'expedie') return 'neutral';
  if (key === 'en_attente') return 'warning';
  if (key === 'en_cours' || key === 'nouvelle_prevision') return 'warning';
  if (key === 'pas_encore') return 'danger';
  return 'primary';
}
